import icn_line_chart from "assets/images/icons/icn_line_chart.png";
import icn_info_solid from "assets/images/icons/icn_Info_solid.svg";
import { ReactComponent as Clock } from "assets/images/icons/icn_display_clock.svg";
import { useTheme } from "@mui/material/styles";

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Tooltip,
  tooltipClasses,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import WiredCongestionLevelChart from "../../charts/WiredCongestionLevelChart";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#4D4D4D",
    color: theme.palette.primary.white,
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: "16px 20px",
    borderRadius: "8px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

const ActionList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 0.5 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ whiteSpace: "pre-line", fontSize: "12px", mb: 1 }}>
        {`· Note: A port is classified as congested when its individual bandwidth utilization exceeds 95%.​`}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Good — Congested Ports &lt; 1x Threshold
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Wired links are operating with sufficient bandwidth headroom and smooth
        traffic flow.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Warning — 1x ≤ Congested Ports &lt; 2x Threshold​
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px" }}>
        Several ports are saturated. Users on those segments may notice latency
        during large transfers.​
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Identify the traffic source and Top Talkers on the affected ports.​",
            "· Rebalance high-traffic devices across switches or VLANs.​",
          ]}
        />
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Critical — Congested Ports ≥ 2x Threshold​
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px" }}>
        Severe port saturation is degrading application performance and dropping
        packets.​
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Add capacity with Link Aggregation (LACP) or higher-speed interfaces.​",
            "· Verify Storm Control is active and rule out physical-layer issues.​",
          ]}
        />
      </Typography>
    </Box>
  </Box>
);

const WiredCongestionLevel = ({
  loading = false,
  noData = false,
  onClick,
  wiredCongestionChartSeriesData = [],
  wiredCongestionApiChartData,
  wiredCongestionPeakData,
}) => {
  const theme = useTheme();
  const statusColorMap = {
    Good: theme.palette.primary.greenPrimary,
    Warning: theme.palette.primary.orangeThird,
    Critical: theme.palette.primary.redPrimary,
  };

  const isNoData = noData || Number(wiredCongestionApiChartData?.switchCount) === 0;

  //Status
  const status = useMemo(() => {
    const peakCongestedPorts = Math.max(
      ...(wiredCongestionApiChartData?.series ?? []).map(
        ([, value]) => Number(value) || 0,
      ),
    );

    const threshold = Number(wiredCongestionApiChartData?.threshold ?? 0);

    if (peakCongestedPorts >= threshold * 2) {
      return "Critical";
    }

    if (peakCongestedPorts >= threshold) {
      return "Warning";
    }

    return "Good";
  }, [wiredCongestionApiChartData]);

  return (
    <Box
      onClick={isNoData ? undefined : onClick}
      sx={{
        border: `1px solid ${theme.palette.primary.greyThread}`,
        borderRadius: theme.shape.customBorderRadius[8],
        p: 2.5,
        bgcolor: theme.palette.primary.white,
        mb: 3,
        cursor: onClick && !isNoData ? "pointer" : "default",
        "&:hover":
          onClick && !isNoData
            ? {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
              }
            : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={icn_line_chart}
            sx={{ width: "18px", height: "18px", mr: 1 }}
          />

          <Typography
            sx={{
              fontWeight: theme.typography.customFontWeight[2],
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[3],
              mr: 1,
            }}
          >
            Wired Congestion Level
          </Typography>

          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
          </HtmlTooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
              ml: 0.5,
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isNoData ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.subBlue,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            No data available
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: 280 },
              pr: { xs: 0, lg: 5 },
              pb: { xs: 3, lg: 0 },
              borderRight: {
                xs: "none",
                lg: `1px solid ${theme.palette.primary.greyThread}`,
              },
            }}
          >
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.greyPrimary,
                  mb: 0.5,
                }}
              >
                Status
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[6],
                  lineHeight: 1.1,
                  fontWeight: theme.typography.customFontWeight[3],
                  color: statusColorMap[status],
                }}
              >
                {status}
              </Typography>
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.primary.greyThread}`,
                pt: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Peak Congested:
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {wiredCongestionPeakData?.peakhourly ?? "-"}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Most Congested:
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {wiredCongestionApiChartData?.most?.deviceName ?? "-"}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Threshold:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {wiredCongestionApiChartData.threshold}
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              pl: { xs: 0, lg: 5 },
              pt: { xs: 1, lg: 0 },
            }}
          >
            <WiredCongestionLevelChart data={wiredCongestionChartSeriesData} wiredCongestionApiChartData={wiredCongestionApiChartData}/>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WiredCongestionLevel;
